import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Zap, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [recovering, setRecovering] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    const res = await login(email, password);
    setBusy(false);
    if (res.ok) {
      toast.success("Bem-vindo à Cliver!");
      navigate("/");
    } else {
      console.log("Login failed", {
        email,
        error: res.error,
        response: res.response,
      });
      setError(res.error);
    }
  };

  const handleForgotPassword = async () => {
    if (!email) {
      toast.error("Informe seu e-mail para recuperar a senha");
      return;
    }
    setRecovering(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      toast.success(data?.message || "Instruções de recuperação enviadas");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao solicitar recuperação");
    }
    setRecovering(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-950 relative overflow-hidden px-4">
      <div
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage:
            "radial-gradient(circle at 30% 20%, rgba(37, 211, 102, 0.15), transparent 50%), radial-gradient(circle at 70% 80%, rgba(37, 211, 102, 0.08), transparent 50%)",
        }}
      />
      <div className="relative w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-xl bg-[#25D366] flex items-center justify-center">
            <Zap className="w-6 h-6 text-slate-950" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-2xl font-bold text-slate-50" style={{ fontFamily: "Outfit" }}>
              Cliver CRM
            </div>
            <div className="text-xs uppercase tracking-widest text-slate-500">
              Omnichannel Conversational Sales
            </div>
          </div>
        </div>

        <div className="bg-slate-900/60 backdrop-blur border border-slate-800 rounded-2xl p-7 shadow-2xl">
          <h1 className="text-2xl font-bold text-slate-50 mb-1" style={{ fontFamily: "Outfit" }}>
            Entrar
          </h1>
          <p className="text-sm text-slate-400 mb-6">Acesse seu funil de vendas</p>

          <form onSubmit={onSubmit} className="space-y-4">
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Email</Label>
              <Input
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="bg-slate-800 border-slate-700 text-slate-100 focus:border-[#25D366] mt-1.5 h-11"
              />
            </div>
            <div>
              <Label className="text-slate-300 text-xs uppercase tracking-wider">Senha</Label>
              <Input
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="bg-slate-800 border-slate-700 text-slate-100 focus:border-[#25D366] mt-1.5 h-11"
              />
              <div className="mt-2 text-right">
                <button
                  type="button"
                  onClick={handleForgotPassword}
                  disabled={recovering}
                  className="text-xs text-[#25D366] hover:underline disabled:opacity-60"
                >
                  Esqueci minha senha
                </button>
              </div>
            </div>

            {error && (
              <div data-testid="login-error" className="text-sm text-red-400 bg-red-500/10 border border-red-500/30 px-3 py-2 rounded-lg">
                {error}
              </div>
            )}

            <Button
              data-testid="login-submit"
              type="submit"
              disabled={busy}
              className="w-full h-11 bg-[#25D366] hover:bg-[#1EAC52] text-slate-950 font-semibold"
            >
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Entrar no Funil"}
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
}

